import { countryList } from 'helper/countries';
import { nanoid } from 'nanoid';
import * as yup from 'yup';

import {
    childNodeMapper,
    ENABLED_ADDITIONAL_CHECKS,
    GROUP_NODE_WIDTH,
    parentNodeMapper,
    VERIFICATION_LABEL,
    VERIFICATIONS_VALUES,
} from '../constants';

/**
 * @param {Array} settings An array of objects, where each object has a settings metadata
 * @param {Array} settingValues An array of objects, where each object has the current value of a setting
 * @returns Object
 */
export const mergeSettingsAndValues = (settings, settingValues) => {
    const mergedSettings = settings.map((setting) => {
        let existingSetting = null;

        if (Array.isArray(settingValues)) {
            existingSetting = settingValues.find(
                (settingValue) => settingValue?.name === setting.name
            );
        }
        return { ...setting, value: existingSetting ? existingSetting.value : null };
    });

    return mergedSettings;
};

export const getDefaultEnhancedChecksState = () => ({
    sevenYearCheck: false,
    verifyId: false,
});

export const getEnhancedCheckStateFromSetting = (enabledAdditionalCheckSetting = []) => {
    const state = getDefaultEnhancedChecksState();

    if (Array.isArray(enabledAdditionalCheckSetting)) {
        if (enabledAdditionalCheckSetting.includes(ENABLED_ADDITIONAL_CHECKS.VALUES.verifyId)) {
            state.verifyId = true;
        }
        if (
            enabledAdditionalCheckSetting.includes(ENABLED_ADDITIONAL_CHECKS.VALUES.sevenYearCheck)
        ) {
            state.sevenYearCheck = true;
        }
    }

    return state;
};

export const guestyActivateFormValidationSchema = () =>
    yup.object({
        token: yup.string().required('Integration token required.'),
    });

export const hostawayActivateFormValidationSchema = () =>
    yup.object({
        apiKey: yup.string().required('API Key is required.'),
        accountId: yup.string().required('Account ID is required.'),
    });

export const lodgifyActivateFormValidationSchema = () =>
    yup.object({
        apiKey: yup.string().required('API Key is required.'),
    });

export const _findSettingValueByName = (existingSettings) => (name) => {
    let item = existingSettings.find((setting) => setting.name === name);
    return item ? item.value : null;
};

export const prepareListingsTableHeader = (labels) => {
    return labels.map((label) => ({
        id: nanoid(),
        label,
    }));
};

/**
 * The API stores the channels we should SKIP (`disabled_booking_channels`), while the
 * drawer shows the channels we should PROCESS. These two helpers invert between them.
 *
 * @param {Array<{value: string}>} channels Full frontend channel list
 * @param {Array<string>} disabledValues Excluded channel values returned by the API
 * @returns {Array<string>} Values that should render as checked
 */
export const getSelectedChannelValues = (channels = [], disabledValues = []) => {
    const excluded = new Set(disabledValues);
    return channels.map(({ value }) => value).filter((value) => !excluded.has(value));
};

/**
 * @param {Array<{value: string}>} channels Full frontend channel list
 * @param {Array<string>} selectedValues Values currently checked in the drawer
 * @returns {Array<string>} Values to send back as `disabled_booking_channels`
 */
export const getDisabledChannelValues = (channels = [], selectedValues = []) => {
    const selected = new Set(selectedValues);
    return channels.map(({ value }) => value).filter((value) => !selected.has(value));
};

/**
 * @param {Array<{label: string}>} channels Full frontend channel list
 * @param {string} term Raw search input
 * @returns {Array} Channels whose label contains the term, case-insensitively
 */
export const filterChannelsBySearch = (channels = [], term = '') => {
    const searchTerm = term.trim().toLowerCase();
    if (!searchTerm) return channels;

    return channels.filter(({ label }) => label.toLowerCase().includes(searchTerm));
};

const getCountryName = (code) => {
    const country = countryList.find((country) => country.value === code);
    return country ? country.label : code;
};

/*
@condition: check.enabled ?? check ( or where check.enabled === undefined)
Note for the condition: In the workflow preview component, two types of data are passed: one from the fetchOneWorkflow API and the other from the fetchAllWorkflow API. The fetchAllWorkflow API response does not have the enabled property; all the enabled checks are added as it is. So if enabled is not present, then will return the check as it is, so workflow preview will be shown for both kind of data
*/

export const returnEnabledCountries = (countries) => {
    if (!countries) return null;

    if (countries === 'all') {
        return 'All countries';
    } else {
        const countryNames = countries.map(getCountryName);
        return countryNames.join(', ');
    }
};

export const getVerificationConfig = (identityProof) => {
    if (!identityProof) return null;

    // return the verification configuration AND, OR
    const isMandatory = identityProof
        ?.filter((verification) => {
            if (verification.verification_type === VERIFICATIONS_VALUES.QUICK_VERIFY) return false;
            if (verification.enabled === undefined) {
                return verification;
            } else {
                return verification.enabled;
            }
        })
        ?.every((verification) => verification.mandatory === true);
    const verificationConfig = isMandatory ? 'AND' : 'OR';

    return verificationConfig;
};

export const formatPreIdentityChecksData = (preIdentityChecks) => {
    if (!preIdentityChecks) return null;

    const preIdentityChecksList = preIdentityChecks
        ?.filter((check) => check.enabled ?? check)
        .map((check) => VERIFICATION_LABEL[check.verification_type])
        ?.join(', ');

    return preIdentityChecksList;
};

export const formatIdentityProofData = (identityProof) => {
    if (!identityProof) return null;

    // return the enabled verification with attempts
    const allowedVerificationTypes = [
        VERIFICATIONS_VALUES.GOVT_ID,
        VERIFICATIONS_VALUES.KBA,
        VERIFICATIONS_VALUES.FAO,
    ];

    const verificationList = identityProof
        .filter((verification) => {
            const isAllowedType = allowedVerificationTypes.includes(verification.verification_type);
            if (!isAllowedType) return false;
            return verification.enabled ?? verification;
        })
        .map((verification) => {
            let formattedText;
            const type = VERIFICATION_LABEL[verification.verification_type];
            const livenessCheck = verification?.metadata?.livenessCheck;

            if (verification.verification_type === VERIFICATIONS_VALUES.GOVT_ID) {
                formattedText = `${type} - ${verification.attempts}`;

                if (livenessCheck?.enabled) {
                    formattedText += `, Selfie - ${livenessCheck.attempts}`;
                }
            } else {
                formattedText = `${type} - ${verification.attempts}`;
            }

            return formattedText;
        })
        .filter(Boolean);

    const verificationConfig = getVerificationConfig(identityProof);

    // add quick verify as a separate object if enabled
    const quickVerify = identityProof
        .filter(
            (check) =>
                check.verification_type === VERIFICATIONS_VALUES.QUICK_VERIFY &&
                (check.enabled ?? check)
        )
        .map((check) => VERIFICATION_LABEL[VERIFICATIONS_VALUES.QUICK_VERIFY])
        .toString();

    return { verificationList, verificationConfig, quickVerify };
};

/**
 * Returns `true` if the criminal check fallback UI should be displayed.
 * Shows the fallback when:
 * - Workflow has enabled countries (and is NOT USA-only)
 * - 7-Year Criminal Check is enabled
 * - Global Watchlist Check (GWC) is NOT enabled
 */
export const shouldShowCriminalCheckFallback = (workflowData) => {
    const countries = workflowData?.workflow?.enabled_countries || workflowData?.enabled_countries;
    if (!countries) return false;

    const isOnlyUSA = Array.isArray(countries) && countries.length === 1 && countries[0] === 'USA';
    if (isOnlyUSA) return false;

    const additionalChecks = workflowData?.additional_checks ?? [];

    const sevenYearEnabled = additionalChecks.some(
        (check) =>
            check.check_type === VERIFICATIONS_VALUES.SEVEN_YEAR_CRIMINAL_CHECK &&
            (check.enabled ?? true)
    );
    if (!sevenYearEnabled) return false;

    const gwcEnabled = additionalChecks.some(
        (check) => check.check_type === VERIFICATIONS_VALUES.GWC && (check.enabled ?? true)
    );
    if (gwcEnabled) return false;

    return true;
};

export const formatAdditionalChecksData = (additionalCheck) => {
    if (!additionalCheck) return null;

    const enabledAdditionalChecks = additionalCheck
        ?.filter((check) => check.enabled ?? check)
        ?.map((check) => VERIFICATION_LABEL[check.check_type])
        ?.join(', ');

    return enabledAdditionalChecks;
};

export const calculatePositionX = (
    i,
    childCount,
    childWidth,
    GROUP_NODE_WIDTH,
    totalChildrenWidth
) => {
    const SPACING = 100;

    const calculateForOneChild = () => GROUP_NODE_WIDTH / 2 - childWidth / 2;

    const calculateForTwoChildren = () =>
        i === 0 ? -(totalChildrenWidth / 2 - SPACING / 2) : totalChildrenWidth / 2;

    const calculateForThreeChildren = () => {
        if (i === 0) return -totalChildrenWidth / 2;
        if (i === 1) return GROUP_NODE_WIDTH / 2 - childWidth / 2;
        return totalChildrenWidth / 2 + SPACING;
    };

    const calculateForFourChildren = () => {
        if (i === 0) return -(totalChildrenWidth - GROUP_NODE_WIDTH);
        if (i === 1) return -(GROUP_NODE_WIDTH / 2 + SPACING);
        if (i === 2) return GROUP_NODE_WIDTH / 2 + SPACING;
        return totalChildrenWidth - GROUP_NODE_WIDTH;
    };

    switch (childCount) {
        case 1:
            return calculateForOneChild();
        case 2:
            return calculateForTwoChildren();
        case 3:
            return calculateForThreeChildren();
        case 4:
            return calculateForFourChildren();
        default:
            return 0;
    }
};

export const createGroupNode = (node, index) => {
    const groupId = `group-${node}`;

    return {
        id: groupId,
        position: { x: 0, y: 200 + index * 350 },
        style: {
            width: GROUP_NODE_WIDTH,
            height: 250,
            backgroundColor: 'transparent',
            border: 'none',
        },
        selectable: false,
        measured: {
            width: GROUP_NODE_WIDTH,
            height: 250,
        },
    };
};

export const createParentNode = (node, groupId) => {
    const parentId = `parent-${node}`;

    return {
        id: parentId,
        type: 'method',
        data: parentNodeMapper[node],
        position: { x: GROUP_NODE_WIDTH / 2 - parentNodeMapper[node]?.measured?.width / 2, y: 0 },
        parentId: groupId,
        measured: parentNodeMapper[node]?.measured,
    };
};

export const createChildNodes = (node, groupId, childId, data) => {
    const childNodes = parentNodeMapper[node].children.filter((child) => data.includes(child));
    const totalChildrenWidth = childNodes.reduce(
        (total, child) => total + childNodeMapper[child]?.measured?.width,
        0
    );

    return childNodes.map((childNode, i) => {
        const positionX = calculatePositionX(
            i,
            childNodes.length,
            childNodeMapper[childNode]?.measured?.width,
            GROUP_NODE_WIDTH,
            totalChildrenWidth
        );

        return {
            id: `${childId}${i + 1}`,
            type: 'status',
            data: childNodeMapper[childNode].data,
            position: { x: positionX, y: 160 },
            parentId: groupId,
            measured: childNodeMapper[childNode]?.measured,
        };
    });
};

/**
 * Coerces an api value to a finite number.
 * @param {*} value The value to coerce
 * @returns {number|null} The number, or null when it is not a finite numeric value
 */
export const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

export const createEdgesBetweenNodes = (node, parentId, children) => {
    return children.reduce((edges, child, i) => {
        const baseEdge = {
            id: `parent-${node}-to-child-${node}-${i + 1}`,
            source: parentId,
            target: child.id,
            type: 'step',
            style: { stroke: '#9E77ED', strokeWidth: 2 },
        };
        edges.push(baseEdge);

        // add edge between consecutive children
        if (i > 0) {
            const newEdge = {
                id: `child-${node}-${i}-to-child-${node}-${i + 1}`,
                source: children[i - 1].id,
                target: children[i].id,
                sourceHandle: 'right',
                targetHandle: 'left',
                type: 'or',
            };
            edges.push(newEdge);
        }

        return edges;
    }, []);
};
